import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './InvigilatorFlagged.css'
import LoadingScreen from '../components/LoadingScreen'

const InvigilatorFlagged = () => {
  const [flaggedAttempts, setFlaggedAttempts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const history = useHistory()

  useEffect(() => {
    let isMounted = true

    const fetchFlaggedAttempts = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch('/api/invigilator/flagged-attempts', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          if (isMounted) {
            setFlaggedAttempts(data.flagged_attempts || [])
          }
        } else {
          if (isMounted) {
            setError('Failed to load flagged attempts')
          }
        }
      } catch (err) {
        if (isMounted) {
          setError('Network error')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchFlaggedAttempts()

    return () => {
      isMounted = false
    }
  }, [])

  const getSeverityLevel = (count) => {
    if (count >= 5) return 'critical'
    if (count >= 3) return 'high'
    if (count >= 1) return 'medium'
    return 'low'
  }

  const getFilteredAttempts = () => {
    if (filter === 'all') return flaggedAttempts
    return flaggedAttempts.filter(attempt => getSeverityLevel(attempt.violation_count) === filter)
  }

  const handleReview = (attemptId) => {
    history.push(`/invigilator/attempt/${attemptId}`)
  }

  if (loading) {
    return <LoadingScreen message="Loading Flagged Attempts" />
  }

  const filteredAttempts = getFilteredAttempts()

  return (
    <div className="flagged-container">
      <header className="page-header">
        <div>
          <h1>Flagged Attempts</h1>
          <p className="subtitle">Review test attempts with violations</p>
        </div>
        <button onClick={() => history.push('/invigilator-dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="flagged-stats">
        <div className="stat-card critical">
          <span className="stat-number">
            {flaggedAttempts.filter(a => getSeverityLevel(a.violation_count) === 'critical').length}
          </span>
          <span className="stat-label">Critical (5+ violations)</span>
        </div>
        <div className="stat-card high">
          <span className="stat-number">
            {flaggedAttempts.filter(a => getSeverityLevel(a.violation_count) === 'high').length}
          </span>
          <span className="stat-label">High (3-4 violations)</span>
        </div>
        <div className="stat-card medium">
          <span className="stat-number">
            {flaggedAttempts.filter(a => getSeverityLevel(a.violation_count) === 'medium').length}
          </span>
          <span className="stat-label">Medium (1-2 violations)</span>
        </div>
      </div>

      <div className="filter-section">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({flaggedAttempts.length})
        </button>
        <button
          className={`filter-btn ${filter === 'critical' ? 'active' : ''}`}
          onClick={() => setFilter('critical')}
        >
          Critical
        </button>
        <button
          className={`filter-btn ${filter === 'high' ? 'active' : ''}`}
          onClick={() => setFilter('high')}
        >
          High
        </button>
        <button
          className={`filter-btn ${filter === 'medium' ? 'active' : ''}`}
          onClick={() => setFilter('medium')}
        >
          Medium
        </button>
      </div>

      <div className="flagged-content">
        {filteredAttempts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✅</div>
            <h3>No Flagged Attempts</h3>
            <p>All test attempts are clean</p>
          </div>
        ) : (
          <div className="flagged-list">
            {filteredAttempts.map((attempt) => (
              <div key={attempt.id} className={`flagged-card severity-${getSeverityLevel(attempt.violation_count)}`}>
                <div className="flagged-header">
                  <div className="student-details">
                    <h3>{attempt.student_name}</h3>
                    <p>{attempt.student_email}</p>
                  </div>
                  <div className={`severity-badge ${getSeverityLevel(attempt.violation_count)}`}>
                    {getSeverityLevel(attempt.violation_count).toUpperCase()}
                  </div>
                </div>

                <div className="test-details">
                  <h4>{attempt.test_title}</h4>
                  <div className="attempt-meta">
                    <span>Date: {new Date(attempt.created_at).toLocaleString()}</span>
                    <span>Score: {attempt.percentage}%</span>
                  </div>
                </div>

                <div className="violations-section">
                  <div className="violations-header">
                    <span className="violations-count">
                      <span className="warning-icon">⚠️</span>
                      {attempt.violation_count} Violations Detected
                    </span>
                  </div>
                  {attempt.violations && attempt.violations.length > 0 && (
                    <ul className="violations-list">
                      {attempt.violations.map((violation, index) => (
                        <li key={index} className="violation-item">
                          <span className="violation-type">{violation.type}</span>
                          <span className="violation-time">
                            {new Date(violation.timestamp).toLocaleTimeString()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  onClick={() => handleReview(attempt.id)}
                  className="review-btn"
                >
                  Review Details
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default InvigilatorFlagged
